import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Link,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { useTranslation } from 'react-i18next';
import { EmployeeDocumentService } from '../../services/employee-document.service';
import {
    ALL_DOCUMENT_TYPES,
    EmployeeDocumentType,
    IEmployeeDocument,
} from '../../interfaces/employee-document.interface';

interface EmployeeDocumentsProps {
    userId?: string;
}

const EmployeeDocuments: React.FC<EmployeeDocumentsProps> = ({ userId }) => {
    const [documents, setDocuments] = useState<IEmployeeDocument[]>([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState<Partial<Record<EmployeeDocumentType, boolean>>>({});
    const { user } = useAuth();
    const toast = useToast();
    const { t } = useTranslation(['users', 'common']);
    const isAdminView = Boolean(userId);
    const canAdminUpload = isAdminView && user?.roles === 'InstitutionAdmin';

    const fileInputRefs = useRef<Partial<Record<EmployeeDocumentType, HTMLInputElement | null>>>({});

    useEffect(() => {
        fetchDocuments();
    }, [userId]);

    const fetchDocuments = async () => {
        setLoading(true);
        try {
            const docs = userId
                ? await EmployeeDocumentService.getDocumentsByUserId(userId)
                : await EmployeeDocumentService.getMyDocuments();
            setDocuments(docs);
        } catch (err) {
            toast.showApiError(err, t('employeeDocuments.loadFailed'));
        } finally {
            setLoading(false);
        }
    };

    const docMap = new Map(documents.map(d => [d.documentType, d]));

    const handleUploadClick = (type: EmployeeDocumentType) => {
        fileInputRefs.current[type]?.click();
    };

    const handleFileChange = (type: EmployeeDocumentType) => async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        e.target.value = '';

        setUploading(prev => ({ ...prev, [type]: true }));
        try {
            const uploaded = isAdminView && userId
                ? await EmployeeDocumentService.uploadDocumentForUser(userId, type, file)
                : await EmployeeDocumentService.uploadDocument(type, file);
            setDocuments(prev => {
                const existing = prev.find(d => d.documentType === type);
                if (existing) {
                    return prev.map(d => d.documentType === type ? uploaded : d);
                }
                return [...prev, uploaded];
            });
            toast.showSuccess(t('employeeDocuments.uploaded'));
        } catch (err) {
            toast.showApiError(err, t('employeeDocuments.uploadFailed'));
        } finally {
            setUploading(prev => ({ ...prev, [type]: false }));
        }
    };

    const handleDownload = async (doc: IEmployeeDocument) => {
        try {
            await EmployeeDocumentService.downloadDocument(doc.id, doc.originalFileName);
        } catch (err) {
            toast.showApiError(err, t('employeeDocuments.downloadFailed'));
        }
    };

    return (
        <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom>{t('employeeDocuments.title')}</Typography>
            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: 220, fontWeight: 600 }}>{t('employeeDocuments.columns.documentType')}</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>{t('employeeDocuments.columns.file')}</TableCell>
                            <TableCell sx={{ width: 200, fontWeight: 600 }}>{t('employeeDocuments.columns.action')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {ALL_DOCUMENT_TYPES.map(type => {
                            const doc = docMap.get(type);
                            const isUploading = uploading[type];

                            return (
                                <TableRow key={type}>
                                    <TableCell>{t(`documentTypes.${type}`)}</TableCell>
                                    <TableCell>
                                        {doc ? (
                                            <Link
                                                component="button"
                                                underline="hover"
                                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                                                onClick={() => handleDownload(doc)}
                                            >
                                                <AttachFileIcon fontSize="small" />
                                                {doc.originalFileName}
                                            </Link>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                {t('employeeDocuments.notUploaded')}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={1}>
                                            {doc && (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={<DownloadIcon />}
                                                    onClick={() => handleDownload(doc)}
                                                >
                                                    {t('common:actions.download')}
                                                </Button>
                                            )}
                                            {((!isAdminView && !doc) || canAdminUpload) && (
                                                isUploading ? (
                                                    <CircularProgress size={24} />
                                                ) : (
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        startIcon={<UploadFileIcon />}
                                                        onClick={() => handleUploadClick(type)}
                                                    >
                                                        {doc ? t('employeeDocuments.replace') : t('common:actions.upload')}
                                                    </Button>
                                                )
                                            )}
                                            <input
                                                type="file"
                                                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                                style={{ display: 'none' }}
                                                ref={el => { fileInputRefs.current[type] = el; }}
                                                onChange={handleFileChange(type)}
                                            />
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <CircularProgress size={24} />
                </Box>
            )}
        </Box>
    );
};

export default EmployeeDocuments;
